import ViewContext from '../Repository/ViewContext';
import IEpiserverContext from '../Core/IEpiserverContext';

export interface ISpaNavigationHandler {
  initialize(): void;
  destroy(): void;
}


export class SpaNavigationHandler implements ISpaNavigationHandler {
  private _clickHandler?: (event: MouseEvent) => void;
  private _context: IEpiserverContext;

  // Handle browser back / forward buttons
  private _popStateHandler = (event: PopStateEvent) => {
    console.debug('[SpaNavigationHandler] [PopStateEvent]');
    let newPath: string = window.location.pathname;
    const basePath = this._context.getSpaBasePath() || '';
    if (basePath.length > 0 && newPath.substr(0, basePath.length) === basePath) {
      newPath = newPath.substr(basePath.length);
    }
    console.debug(`[SpaNavigationHandler] loadContentByPath ${newPath}`);
    this._context.loadContentByPath(newPath).then((iContent) => {
      this._context.dispatch(ViewContext.updateCurrentPath(newPath));
    });
  };

  constructor(context: IEpiserverContext) {
    this._context = context;
  }

  /**
   * Listen for bfcache restore and log if it happens. If you want to force reload, uncomment location.reload().
   */
  private _pageShowHandler = (event: PageTransitionEvent) => {
    if (event.persisted) {
      // Page was restored from bfcache
      if (this._context.isDebugActive()) {
        console.log('[SpaNavigationHandler] Page restored from bfcache');
      }
      // window.location.reload(); // Uncomment to force reload on bfcache restore
    }
  };

  public initialize(): void {
    this._clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      let anchor: HTMLAnchorElement | null = null;
      let newPath: string = '';
      const currentUrl = new URL(window.location.href);

      // Check if target is an anchor or find parent anchor
      function findFirstParentAnchor(el) {
        while (el && el !== document.body) {
          if (el.tagName === 'A') {
            const rawHref = el.getAttribute('href');
            const resolvedHref = el.href; // This is the fully resolved absolute URL
            if (resolvedHref) {
              el.setAttribute('data-resolved-href', resolvedHref);
            }
            return el;
          }
          el = el.parentElement;
        }
        return null;
      }

      anchor = findFirstParentAnchor(target);

      if (anchor) {
        const rawHref = anchor.getAttribute('href') || '';
        const resolvedHref = anchor.getAttribute('data-resolved-href') || anchor.href || '';
        const hrefForCheck = `${rawHref} ${resolvedHref}`;
        const isAssetLink =
          /\.pdf($|\?)/i.test(rawHref) ||
          /\.pdf($|\?)/i.test(resolvedHref) ||
          hrefForCheck.includes('/globalassets') ||
          hrefForCheck.includes('/contentassets');

        if (isAssetLink) {
          event.preventDefault();
          const pathForEpi = rawHref.startsWith('http') ? new URL(rawHref).pathname : rawHref;
          const assetUrl = pathForEpi
            ? this._context.getEpiserverUrl(pathForEpi)
            : resolvedHref;
          const openInNewWindow = anchor.target?.toLowerCase() === '_blank';

          if (openInNewWindow) {
            window.open(assetUrl, '_blank', 'noopener,noreferrer');
          } else {
            window.location.assign(assetUrl);
          }
          return false;
        }

        if (anchor.target && anchor.target.toLowerCase() !== '_self') {
          return;
        }
      }

      const resolvedHref = anchor?.getAttribute('data-resolved-href');

      if (resolvedHref) {
        const targetUrl = new URL(resolvedHref);

        // Only act if we remain on the same domain
        if (targetUrl.origin === currentUrl.origin) {
          newPath = targetUrl.pathname;
        }
      }

      // Ignore navigation to same path
      if (newPath === currentUrl.pathname && resolvedHref === currentUrl.href) {
        if (this._context.isDebugActive()) {
          console.warn('Ignoring navigation to same path');
        }
        event.preventDefault();
        return false;
      }

      if (newPath) {
        const basePath = this._context.getSpaBasePath() || '';
        if (basePath && newPath.startsWith(basePath)) {
          newPath = newPath.substring(basePath.length);
          if (!newPath.startsWith('/')) {
            newPath = '/' + newPath; // Ensure absolute path
          }
        }

        this._context.loadContentByPath(newPath).then((iContent) => {
          this._context.dispatch(ViewContext.updateCurrentPath(newPath));
          this.pushPath(newPath);
          if (typeof window.scrollTo === 'function') {
            window.scrollTo(0, 0);
          }
        });
        event.preventDefault();
        return false;
      }
    };

    window.addEventListener('click', this._clickHandler);
    window.addEventListener('pageshow', this._pageShowHandler);
    window.addEventListener('popstate', this._popStateHandler);
  }

  public destroy(): void {
    if (this._clickHandler) {
      window.removeEventListener('click', this._clickHandler);
      this._clickHandler = undefined;
    }
    window.removeEventListener('pageshow', this._pageShowHandler);
    window.removeEventListener('popstate', this._popStateHandler);
  }

  private pushPath(newPath: string): void {
    if (window.history && window.history.pushState) {
      const basePath = this._context.getSpaBasePath() || '';
      const spaPath = (basePath !== '/' ? basePath : '') + newPath;
      window.history.pushState({}, '', spaPath);
    }
  }
}

export default SpaNavigationHandler;
